// Globals
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { getStocksImportTemplate, importStocks } from '../../feature';

// Components
import { StyledBackButton, StyledChevronIcon, StyledPageTitle, StyledWrapper } from '../../components/Styled';
import StocksImport from '../../components/StocksImport/StocksImport';
import { StyledHeaderWrap } from '../../components/PreparingStep/PreparingStepStyles';
import { showErrorToast, showSuccessToast, useForm } from '@/components';
import SuccessScreen from '../../components/StocksImport/SuccessScreen';

// Models
import { IApiError } from '@/models/apiError.model';
import { ExportStatusEnum, ITemplateExportProgress } from '@/models/productsImport.model';

// API
import { ProductsImportAPI } from '@/api';
import { formatApiError, isHttpClientError } from '@/utils';

// Modules
import { useCategoriesAutocomplete } from '@/modules/Categories';
import { getPartnersOptions, usePartnersListSearch } from '@/modules/Partners';
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useBoolean } from '@/hooks';
import { getCategoriesOptions, ImportStocksFieldNames, TImportStocksForm } from '../../helpers';
import { StocksImportDTO } from '../../dtos';

// Pause between the end of one status request and the start of the next.
const POLLING_INTERVAL = 5000;
// A single status request that takes longer than this is aborted and counted as a failure.
const POLLING_REQUEST_TIMEOUT = 15000;
// Consecutive failed status requests (network error, timeout, 5xx) before giving up.
const POLLING_MAX_FAILURES = 3;
// Stop waiting for the export altogether after this long.
const POLLING_MAX_DURATION = 30 * 60 * 1000;

const StocksImportContainer = () => {
  const { t } = useTranslation(['products']);
  const [form] = useForm();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templateProgress, setTemplateProgress] = useState<ITemplateExportProgress | null>(null);
  const dispatch = useAppDispatch();
  const { value: success, setTrue: setSuccess, setFalse: resetSuccess } = useBoolean();
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({ hasChildren: '0' });
  const { partnersList, searchPartners } = usePartnersListSearch();
  const { canManage } = usePermissions();
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    requestRef.current?.abort();
    requestRef.current = null;
  }, []);

  const finishPolling = useCallback(() => {
    stopPolling();
    setIsTemplateLoading(false);
    setTemplateProgress(null);
  }, [stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const pollExportStatus = useCallback(
    (exportId: string) => {
      stopPolling();
      setIsTemplateLoading(true);
      setTemplateProgress(null);

      const startedAt = Date.now();
      let failures = 0;

      // The next request is scheduled only after the previous one has settled, so slow
      // responses never pile up into parallel requests.
      const poll = async () => {
        pollingRef.current = null;

        if (Date.now() - startedAt > POLLING_MAX_DURATION) {
          finishPolling();
          showErrorToast({ message: t('stocks_import_template_timeout') });
          return;
        }

        const controller = new AbortController();
        requestRef.current = controller;

        try {
          const { data: response } = await ProductsImportAPI.getExportStatus(exportId, {
            signal: controller.signal,
            timeout: POLLING_REQUEST_TIMEOUT,
          });

          if (controller.signal.aborted) return;

          failures = 0;
          const job = response.data;

          if (job.status === ExportStatusEnum.FINISHED) {
            finishPolling();
            if (job.result_file) {
              window.open(job.result_file, '_blank');
            }
            return;
          }

          if (job.status === ExportStatusEnum.FAILED) {
            finishPolling();
            showErrorToast({ message: job.error_message || t('products_import_template_error') });
            return;
          }

          setTemplateProgress({ progress: job.progress_count ?? 0, total: job.total_count ?? 0 });
          pollingRef.current = setTimeout(poll, POLLING_INTERVAL);
        } catch (e) {
          if (controller.signal.aborted) return;

          // 4xx (no access, export not found) won't fix itself — stop right away.
          const status = isHttpClientError(e) ? e.response?.status : undefined;
          failures += 1;

          if ((status && status < 500) || failures >= POLLING_MAX_FAILURES) {
            finishPolling();
            // No status = network error or request timeout: the axios message isn't user-friendly.
            showErrorToast({
              message: status ? formatApiError(e).message : t('stocks_import_template_polling_error'),
            });
            return;
          }

          pollingRef.current = setTimeout(poll, POLLING_INTERVAL);
        } finally {
          if (requestRef.current === controller) {
            requestRef.current = null;
          }
        }
      };

      pollingRef.current = setTimeout(poll, POLLING_INTERVAL);
    },
    [stopPolling, finishPolling, t],
  );

  const onFileReset = async () => {
    form.resetFields([ImportStocksFieldNames.FILE]);
    setSelectedFile(undefined);
  };

  const handleSubmit = async (values: TImportStocksForm) => {
    const dto = new StocksImportDTO(values);
    await dispatch(importStocks(dto)).unwrap();
    setSelectedFile(values[ImportStocksFieldNames.FILE]);
    showSuccessToast({ message: t('products_import_saved_success') });
    setSuccess();
  };

  const onValuesChange = async (values: TImportStocksForm) => {
    if (values[ImportStocksFieldNames.FILE]) setSelectedFile(values[ImportStocksFieldNames.FILE]);
  };

  const onGetTemplate = async (filled = false) => {
    const categoryId = form.getFieldValue(ImportStocksFieldNames.CATEGORY_ID);
    const partnerId = form.getFieldValue(ImportStocksFieldNames.PARTNER_ID);

    if (!partnerId) return showErrorToast({ message: t('products_import_choose_partner') });

    try {
      const { data: job } = await dispatch(
        getStocksImportTemplate({
          category_id: categoryId ?? '',
          partner_id: partnerId,
          filled: filled ? '1' : '0',
        }),
      ).unwrap();
      pollExportStatus(job.id);
    } catch (e) {
      showErrorToast({ message: (e as unknown as IApiError).message });
    }
  };

  const onGetPrefilledTemplate = async () => {
    await onGetTemplate(true);
  };

  return (
    <StyledWrapper>
      <StyledHeaderWrap>
        {success && (
          <StyledBackButton onClick={resetSuccess}>
            <StyledChevronIcon />
            {t('products_import_back')}
          </StyledBackButton>
        )}
        <StyledPageTitle>{t('stocks_import_title')}</StyledPageTitle>
      </StyledHeaderWrap>
      {success ? (
        <SuccessScreen selectedFile={selectedFile} />
      ) : (
        <StocksImport
          onSubmit={handleSubmit}
          onCategoriesSearch={onCategoriesSearch}
          onGetFilledTemplate={onGetPrefilledTemplate}
          formInstance={form}
          selectedFile={selectedFile}
          onPartnerSearch={searchPartners}
          partnersOptions={getPartnersOptions(partnersList ?? [])}
          onFileReset={onFileReset}
          onValuesChange={onValuesChange}
          categoryOptions={getCategoriesOptions(categoriesList ?? [])}
          isTemplateLoading={isTemplateLoading}
          templateProgress={templateProgress}
          canManage={canManage(Resource.IMPORT_PRICE)}
        />
      )}
    </StyledWrapper>
  );
};

export default StocksImportContainer;
